/**
 * Inline distillery form for creating distilleries within bottle form
 */

import { useForm } from 'react-hook-form';
import { Button, Input, Select } from '../common';
import { COUNTRIES } from '../../utils/constants';
import { useCreateDistillery } from '../../hooks/useDistilleries';
import { useToast } from '../../context/ToastContext';
import './InlineDistilleryForm.css';

/**
 * Compact form for quickly adding a new distillery
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Save handler with new distillery
 * @param {Function} props.onCancel - Cancel handler
 * @returns {React.ReactElement} Form element
 */
export default function InlineDistilleryForm({ onSave, onCancel }) {
  const { addToast } = useToast();
  const createDistillery = useCreateDistillery();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      distillery_name: '',
      country: ''
    }
  });

  const onSubmit = async (data) => {
    try {
      const newDistillery = await createDistillery.mutateAsync(data);
      addToast({ type: 'success', message: 'Distillery created successfully' });
      onSave(newDistillery);
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Failed to create distillery' });
    }
  };

  return (
    <div className="inline-distillery-form">
      <h4 className="inline-distillery-form__title">Add New Distillery</h4>
      <div className="inline-distillery-form__fields">
        <Input
          label="Distillery Name"
          required
          error={errors.distillery_name?.message}
          {...register('distillery_name', { required: 'Name is required' })}
        />
        <Select
          label="Country"
          required
          options={COUNTRIES}
          error={errors.country?.message}
          {...register('country', { required: 'Country is required' })}
        />
      </div>
      <div className="inline-distillery-form__actions">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={createDistillery.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          loading={createDistillery.isPending}
          onClick={handleSubmit(onSubmit)}
        >
          Add Distillery
        </Button>
      </div>
    </div>
  );
}
